import type {ColorData} from "../_types/ColorData";
import type {ColorWithStatistics} from "../_types/ColorWithStatistics";
import type {Color} from "./customColor";
import {getColorWeight} from "./getColorWeight";

const WIDTH = 128;
const HEIGHT = 128;

const MIN_TRANSPARENCY_THRESHOLD = 128;

// Cache for canvas and context to avoid recreating them
let canvasCache: HTMLCanvasElement | null = null;
let ctxCache: CanvasRenderingContext2D | null = null;

// Function to get or create canvas and context (SSR-safe)
const getCanvasContext = (): CanvasRenderingContext2D => {
    if (!canvasCache || !ctxCache) {
        if (typeof document === 'undefined') {
            throw new Error('Canvas operations require a browser environment');
        }
        canvasCache = document.createElement("canvas");
        canvasCache.width = WIDTH;
        canvasCache.height = HEIGHT;
        ctxCache = canvasCache.getContext("2d", { willReadFrequently: true })!;
    }
    return ctxCache;
};

export const getColorDataFromImage = (image: HTMLImageElement): ColorData => {

    const ctx = getCanvasContext();
    ctx.drawImage(image, 0, 0, WIDTH, HEIGHT);
    const { data, width, height } = ctx.getImageData(0, 0, WIDTH, HEIGHT);

    const rawColors: Array<Color> = [];
    const uniqueColorKeys: Record<string, number> = {};

    for (let i = 0; i < data.length; i += 4) {

        const rawColor = {
            r: data[i],
            g: data[i + 1],
            b: data[i + 2],
            a: data[i + 3]
        };

        if (rawColor.a < MIN_TRANSPARENCY_THRESHOLD) {
            continue;
        }

        rawColors.push({
            mode: "rgb",
            r: rawColor.r / 255,
            g: rawColor.g / 255,
            b: rawColor.b / 255
        } as Color);

        const normalizedColor: Color = {
            mode: "rgb",
            r: (Math.floor(rawColor.r / 5) * 5) / 255,
            g: (Math.floor(rawColor.g / 5) * 5) / 255,
            b: (Math.floor(rawColor.b / 5) * 5) / 255
        };

        const key = `${normalizedColor.r},${normalizedColor.g},${normalizedColor.b}`;

        if (uniqueColorKeys[key]) {
            uniqueColorKeys[key]++;
        } else {
            uniqueColorKeys[key] = 1;
        }
    }

    const uniqueColors: Array<ColorWithStatistics> = [];
    for (const [key, frequency] of Object.entries(uniqueColorKeys)) {

        const [r,g,b] = key.split(",").map(Number);
        const color: Color = { mode: "rgb", r, g,  b };

        uniqueColors.push({
            ...color,
            frequency,
            weight: getColorWeight(color)
        });
    }

    const filteredUniqueColors = uniqueColors.filter(c => c.weight > 17.5);

    return {
        width,
        height,
        rawColors,
        uniqueColors: filteredUniqueColors.length > 0
            ? filteredUniqueColors : uniqueColors,
        palettes: {}
    };
};